import _ from 'lodash';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { batchActions } from 'redux-batched-actions';
import * as documentsActions from 'containers/Documents/actions';
import * as relationsActions from 'containers/Relations/actions';
import { getActiveLayer } from 'hooks/useActiveLayer';
import * as Models from 'models';
import { stylesComparator } from 'utils/styles/stylesComparator';
import { EditorHook } from '../editor';
import { ctaStylesToSource } from '../utils/styles';
import { SizeResetHook } from './useSizeReset';
import { StylesHook } from './useStyles';

type SaveHookProps = {
  relation: Models.LayeredRegularRelationMap<Models.CallToActionStyles>;
  currentCountry: Immutable.List<string>;
  currentLanguage: Immutable.List<string>;
  isAutoFitContent: boolean;
  editMode: boolean;
  document: Models.CallToActionMap;
  cellHeight: number;
  cellWidth: number;
  layoutRelations: Models.LayeredRelationsMap;
};

export const useSave = (
  props: SaveHookProps,
  stylesHook: StylesHook,
  sizeResetHook: SizeResetHook,
  editorHook: EditorHook,
  link: string,
): void => {
  const {
    relation,
    document,
    currentCountry,
    currentLanguage,
    isAutoFitContent,
    cellHeight,
    cellWidth,
    layoutRelations,
    editMode,
  } = props;

  const isMounting = useRef(true);

  const activeLayer = getActiveLayer();
  const { styles } = stylesHook;
  const { cellHeightBeforeEdit, cellWidthBeforeEdit } = sizeResetHook;

  const dispatch = useDispatch();

  const saveDocument = (): void => {
    const actions: Models.IAction<unknown>[] = [];

    // relation
    const updatedRelation = relation.updateIn(['styles', activeLayer], values => values.withMutations(
      value => ctaStylesToSource(styles, value)
        .set('isAutoFitContent', isAutoFitContent),
    ));

    const areStylesEqual = _.isEqualWith(
      updatedRelation.getIn(['styles', activeLayer]).toJS(),
      relation.getIn(['styles', activeLayer]).toJS(),
      stylesComparator,
    );

    const needsToUpdateLayeredRelations = cellWidthBeforeEdit !== cellWidth
      || cellHeightBeforeEdit !== cellHeight
      || !areStylesEqual;

    if (needsToUpdateLayeredRelations) {
      actions.push(relationsActions.updateLayeredRelations(
        layoutRelations.set(updatedRelation.get('id'), updatedRelation),
      ));
    }

    // document
    let updatedDocument: Models.CallToActionMap = document.withMutations(
      doc => doc
        .set('link', link.trim())
        .set('isMockDocument', false)
        // https://issues.merck.com/browse/DCC-4748
        .set('language', currentLanguage)
        .set('country', currentCountry),
    );
    updatedDocument = editorHook.saveStateToDocument(updatedDocument);

    const needsToUpdateDocument = updatedDocument.get('link') !== document.get('link')
      || updatedDocument.get('name') !== document.get('name')
      || !editorHook.isSameStateInDocument(updatedDocument, document);
    if (needsToUpdateDocument) {
      actions.push(documentsActions.setDocument(updatedDocument));
    }

    if (actions.length !== 0) {
      dispatch(batchActions(actions));
    }
  };

  useEffect(
    () => {
      if (isMounting.current) {
        isMounting.current = false;

        return;
      }
      if (editMode) {
        editorHook.returnFocusToEditor();

        return;
      }

      saveDocument();
    },
    // no other dependencies should be included in dpes argument
    [editMode], // eslint-disable-line react-hooks/exhaustive-deps
  );

};
